import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adsApiCall } from "./api";
import { Loader2, Plus, Trash2, Copy, Pencil, ArrowLeft, Zap, Ticket } from "lucide-react";
import { toast, Toaster } from "sonner";
import CampaignsPanel from "./CampaignsPanel";
import AchievementCelebration from "./AchievementCelebration";
import ReferralShareCard from "./ReferralShareCard";
import { StudioShell } from "../studio/StudioShell";
import { useLanguage } from "../contexts/LanguageContext";
import "./ads-creator.css";

function CouponRedeemBar({ onRedeemed }) {
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [myRef, setMyRef] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adsApiCall.myReferralCoupon();
        setMyRef(r);
      } catch { /* anon or error — silently skip */ }
    })();
  }, []);

  async function redeem() {
    const trimmed = code.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const r = await adsApiCall.redeemCoupon(trimmed);
      toast.success((t.studio_my_toast_credits_added || "+{amount} credits added 🎉").replace("{amount}", r.amount));
      setCode("");
      if (r.my_referral) setMyRef(r.my_referral);
      if (onRedeemed) onRedeemed();
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 404 ? (t.studio_my_toast_not_found || "Code not found")
        : status === 409 ? (e?.response?.data?.detail || t.studio_my_toast_already || "Already redeemed")
        : status === 410 ? (t.studio_my_toast_expired || "Expired or deactivated")
        : (t.studio_my_toast_redeem_fail || "Redeem failed");
      toast.error(msg);
    } finally { setBusy(false); }
  }
  return (
    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr", margin: "12px 0 18px" }}>
      <div
        data-testid="ads-coupon-redeem-bar"
        style={{
          display: "flex", gap: 8, alignItems: "center",
          padding: "10px 14px",
          border: "1px dashed rgba(168,85,247,0.35)",
          borderRadius: 10,
          background: "linear-gradient(90deg,rgba(168,85,247,0.08),rgba(124,58,237,0.08))",
        }}
      >
        <Ticket size={16} style={{ color: "#c084fc" }} />
        <div style={{ color: "var(--ads-text-dim)", fontSize: 13, fontWeight: 600 }}>
          {t.studio_my_have_coupon || "Have a coupon?"}
        </div>
        <input
          data-testid="ads-coupon-redeem-input"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t.studio_my_coupon_placeholder || "ABCD-EFGH-IJKL"}
          onKeyDown={(e) => e.key === "Enter" && redeem()}
          style={{
            flex: 1, minWidth: 140, maxWidth: 260, padding: "6px 10px",
            background: "rgba(0,0,0,0.3)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6,
            fontFamily: "monospace", fontSize: 13, letterSpacing: "0.04em",
          }}
        />
        <button
          data-testid="ads-coupon-redeem-btn"
          onClick={redeem}
          disabled={busy || !code.trim()}
          className="ads-btn ads-btn-primary"
          style={{ padding: "6px 14px", fontSize: 13 }}
        >
          {busy ? "…" : (t.studio_my_redeem || "Redeem")}
        </button>
      </div>
      {myRef && <ReferralShareCard referral={myRef} compact />}
    </div>
  );
}

export default function MyAds() {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const navigate = useNavigate();

  async function refresh() {
    setLoading(true);
    try {
      const r = await adsApiCall.listProjects();
      setProjects(r.projects || []);
      setAuthError(false);
    } catch (e) {
      if (e?.response?.status === 401) setAuthError(true);
      else toast.error(t.studio_my_toast_load_fail || "Failed to load projects");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function del(p) {
    if (!window.confirm((t.studio_my_confirm_delete || 'Delete "{title}"?').replace("{title}", p.title))) return;
    await adsApiCall.deleteProject(p.project_id);
    refresh();
  }
  async function dup(p) {
    const copy = await adsApiCall.duplicateProject(p.project_id);
    navigate(`/ads-creator/editor/${copy.project_id}`);
  }

  return (
    <StudioShell activeKey="/ads-creator/my-ads">
    <div className="ads-root" data-testid="ads-my-ads">
      <Toaster position="top-right" theme="dark" />
      <div className="ads-hero" style={{ padding: "48px 28px 28px" }}>
        <div className="ads-container">
          <h1 className="ads-h1" style={{ fontSize: "clamp(32px, 4.5vw, 54px)" }}>{t.studio_my_title || "My designs"}</h1>
          <p className="ads-lead">{t.studio_my_sub || "All your saved projects and exports in one place."}</p>
        </div>
      </div>

      <div className="ads-section" style={{ paddingTop: 20 }}>
        <div className="ads-container">
          {!authError && <CouponRedeemBar onRedeemed={refresh} />}
          {!authError && <CampaignsPanel />}

          <div className="ads-gallery-head">
            <div style={{ color: "var(--ads-text-dim)", fontSize: 13 }} data-testid="ads-my-count">
              {(projects.length === 1 ? (t.studio_my_count_one || "{n} project") : (t.studio_my_count_many || "{n} projects")).replace("{n}", projects.length)}
            </div>
            <Link to="/ads-creator/instant-campaign" className="ads-btn ads-btn-ghost" data-testid="ads-my-instant" style={{ marginRight: 6 }}>
              <Zap size={16} /> {t.studio_my_instant_campaign || "Instant Campaign"}
            </Link>
            <Link to="/ads-creator/editor" className="ads-btn ads-btn-primary" data-testid="ads-my-new">
              <Plus size={16} /> {t.studio_my_new_ad || "New ad"}
            </Link>
          </div>

          {authError ? (
            <div className="ads-empty-state">
              <div style={{ marginBottom: 12 }}>{t.studio_my_login_to_see || "Log in to see your saved ads."}</div>
              <Link to="/login" className="ads-btn ads-btn-primary">{t.studio_my_login || "Log in"}</Link>
            </div>
          ) : loading ? (
            <div className="ads-empty-state"><Loader2 className="animate-spin" /> {t.studio_my_loading || "Loading…"}</div>
          ) : projects.length === 0 ? (
            <div className="ads-empty-state">
              <div style={{ marginBottom: 12 }}>{t.studio_my_empty_title || "No ads yet."}</div>
              <Link to="/ads-creator/templates" className="ads-btn ads-btn-primary">{t.studio_my_browse || "Browse templates"}</Link>
            </div>
          ) : (
            <div className="ads-template-grid" data-testid="ads-my-grid">
              {projects.map((p) => (
                <div key={p.project_id} className="ads-template-card">
                  <div className="ads-template-preview" style={{ background: p.canvas_json?.background || "#000", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, opacity: 0.7, position: "relative" }}>
                    {p.access === "team" && (
                      <span data-testid={`ads-my-team-badge-${p.project_id}`} style={{
                        position: "absolute", top: 8, left: 8,
                        padding: "3px 8px", borderRadius: 6,
                        background: "linear-gradient(135deg,#4ade80,#0ea5e9)",
                        color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}>{t.studio_my_badge_team || "Team"}</span>
                    )}
                    {p.access === "owner" && p.team_id && (
                      <span data-testid={`ads-my-shared-badge-${p.project_id}`} style={{
                        position: "absolute", top: 8, left: 8,
                        padding: "3px 8px", borderRadius: 6,
                        background: "rgba(74,222,128,0.2)", color: "#4ade80",
                        fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
                        textTransform: "uppercase", border: "1px solid rgba(74,222,128,0.4)",
                      }}>{t.studio_my_badge_shared || "Shared"}</span>
                    )}
                    {p.format} · {p.width}×{p.height}
                  </div>
                  <div className="ads-template-meta">
                    <div>
                      <div className="ads-template-title">{p.title}</div>
                      <div className="ads-template-tag">{new Date(p.updated_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, padding: "0 12px 12px" }}>
                    <button className="ads-btn ads-btn-ghost" style={{ flex: 1, padding: "6px 10px", fontSize: 12 }} onClick={() => navigate(`/ads-creator/editor/${p.project_id}`)} data-testid={`ads-my-edit-${p.project_id}`}>
                      <Pencil size={12} /> {t.studio_my_edit || "Edit"}
                    </button>
                    <button className="ads-btn ads-btn-ghost" style={{ padding: "6px 10px" }} onClick={() => dup(p)} data-testid={`ads-my-dup-${p.project_id}`}>
                      <Copy size={12} />
                    </button>
                    {p.access !== "team" && (
                      <button className="ads-btn ads-btn-ghost" style={{ padding: "6px 10px", color: "#f87171" }} onClick={() => del(p)} data-testid={`ads-my-del-${p.project_id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <AchievementCelebration />
    </div>
    </StudioShell>
  );
}
